import { getLocalStorage } from "../Network/ApiService";
import { ls_vars } from "../Library/Constants";
import { ProjectRole, User } from "Types/User";

// This module is a universal control panel for roles within the system.
// If you need to add a new check for a specific case, add it to the
// permissions_by_role object and specify which roles have access to this functionality.
// You can also expand the list of roles here without having to change every single component.

export const userRolePermissions = {
  "auditlogs:bookings:view": 9,
  "auditlogs:organization:view": 8,
  "auditlogs:project:view": 7,
  "auditlogs:user:view": 6,
  "auditlogs:view": 5,
  "booking:quick_and_bulk_actions": 79,
  "booking:attachment:create": 16,
  "booking:attachment:delete": 18,
  "booking:attachment:update": 17,
  "booking:attachment:view": 15,
  "booking:fields:create": 20,
  "booking:fields:view": 19,
  "booking:override_advanced_booking_constraint": 35,
  "booking:organization_bookings:view": 34,
  "booking:column_view_config_default:update": 196,
  "booking:column_view_config_default:delete": 14,
  "booking:column_view_config:delete": 13,
  "booking:column_view_config:create": 11,
  "booking:column_view_config:view": 10,
  "booking:column_view_config:update": 12,
  "booking:list:filter": 0,
  "booking:list:export": 0,
  "booking:create": 24,
  "booking:delete": 26,
  "booking:delete:own": 76,
  "booking:update:all": 25,
  "booking:update:organization": 75,
  "booking:update:own": 75,
  "booking:move:all": 0,
  "booking:move:own": 0,
  "booking:move:organization": 0,
  "booking:reschedule:all": 0,
  "booking:reschedule:own": 0,
  "booking:reschedule:organization": 0,
  "booking:flexible_fields:create": 22,
  "booking:flexible_fields:view": 21,
  "booking:view": 23,
  "booking:view:details": 23,
  "booking:notes:create": 37,
  "booking:notes:delete": 39,
  "booking:notes:update": 38,
  "booking:notes:view": 36,
  "booking:can_approve": 28,
  "booking:can_cancel": 31,
  "booking:can_checkin": 29,
  "booking:can_checkout": 30,
  "booking:can_decline": 32,
  "booking:can_refuse": 33,
  "booking:can_checkin:undo": 77,
  "booking:can_checkout:undo": 78,
  "booking:can_approve:organization": 0,
  "booking:can_approve:undo": 0,
  "booking:can_checkin:organization": 0,
  "booking:can_checkout:organization": 0,
  "booking:can_decline:organization": 0,
  "booking:can_decline:undo": 0,
  "booking:can_cancel:own": 0,
  "booking:can_cancel:organization": 0,
  "booking:can_cancel:undo": 0,
  "booking:can_refuse:organization": 0,
  "booking:can_refuse:undo": 0,
  "booking:can_update_any_status": 27,
  "object:admin:delete": 40,
  "permissions:list": 1,
  "project:access_point:create": 46,
  "project:access_point:delete": 48,
  "project:access_point:update": 47,
  "project:access_point:view": 45,
  "project:availability:create": 64,
  "project:availability:view": 63,
  "project:availability:update": 65,
  "project:create": 2,
  "project:edit": 3,
  "project:features:update": 59,
  "project:features:view": 58,
  "project:integrations:Update": 62,
  "project:integrations:create": 61,
  "project:integrations:view": 60,
  "project:members:edit": 80,
  "project:members:list": 81,
  "project:members:remove": 82,
  "project:resource:create": 50,
  "project:resource:delete": 52,
  "project:resource:update": 51,
  "project:resource:view": 49,
  "project:settings:create": 55,
  "project:settings:view": 54,
  "project:site:create": 42,
  "project:site:delete": 44,
  "project:site:update": 43,
  "project:site:view": 41,
  "project:site:contact:view": 0,
  "project:site:contact:create": 0,
  "project:site:contact:update": 0,
  "project:site:contact:delete": 0,
  "project:slots:view": 53,
  "project:vehicles:create": 57,
  "project:vehicles:view": 56,
  "project:list": 4,
  "user:create": 74,
  "user:edit": 71,
  "user:invite": 72,
  "user:list": 73,
  "user:list:filter": 83,
  "user:view": 66,
  "vehicle_types:create": 68,
  "vehicle_types:update": 69,
  "vehicle_types:delete": 70,
  "vehicle_types:view": 67,
  "equipment:reports:view": 147,
  "equipment:reports:export": 148,
  "emissions:view": 149,
  "emissions:create": 150,
  "emissions:update": 151,
  "emissions:delete": 152,
  "emissions:reports:view": 153,
  "emissions:reports:export": 154,
  "equipment:view": 155,
  "equipment:create": 156,
  "equipment:update": 157,
  "equipment:delete": 158,
  "register:equipment:view": 159,
  "register:equipment:create": 160,
  "register:equipment:update": 161,
  "register:equipment:delete": 162,
  "equipment:usage:view": 163,
  "equipment:usage:create": 164,
  "equipment:usage:update": 165,
  "equipment:usage:delete": 140,
  "equipment:usage:approve": 141,
  "equipment:usage:attachment:view": 142,
  "equipment:usage:attachment:create": 143,
  "equipment:usage:attachment:update": 144,
  "equipment:usage:attachment:delete": 145,
  "equipment:usage:attachment:download": 146,
  "screen:project:management": 0,
  "screen:user:management": 0,
  "screen:vehicle:tracking": 0,
  "screen:calendar": 0,
  "emissions:approve":200,
};

export type PermissionCode = keyof typeof userRolePermissions;

export const useRolePermission = () => {
  const USER: User = getLocalStorage(ls_vars.USER_DATA);
  const PROJECT_PROLE: ProjectRole = getLocalStorage(ls_vars.PROJECT_ROLE);
  const PROJECT_PROLE_PERMISSION = PROJECT_PROLE?.permissions || [];
  const ORGANIZATION_ROLE = USER?.organizationRole?.name || "";

  const IS_ORG_ADMIN = ORGANIZATION_ROLE === "admin";
  const IS_GLOBAL_ADMIN = getLocalStorage(ls_vars.USER_IS_ADMIN) || false;

  const checkPermission = (permsToCheck: PermissionCode[]) => {
    const hasPermission = permsToCheck.every((perm) =>
      PROJECT_PROLE_PERMISSION.includes(perm)
    );
    return IS_GLOBAL_ADMIN || hasPermission;
  };

  return {
    user: USER,
    projectRole: PROJECT_PROLE,
    orgRole: ORGANIZATION_ROLE,
    IS_ORG_ADMIN,
    IS_GLOBAL_ADMIN,
    checkPermission,
  };
};
